/**
 * Spec-Driven Development (SDD) Types and Interfaces
 * Based on VegaDigital Constitution and spec-kit workflow
 */

// Core SDD Phase Definition
export enum SDDPhase {
  CONSTITUTION = 'constitution',
  SPECIFY = 'specify',
  PLAN = 'plan',
  TASKS = 'tasks',
  CLARIFY = 'clarify',
  ANALYZE = 'analyze',
  CHECKLIST = 'checklist',
  IMPLEMENT = 'implement'
}

// Phase Status for tracking progress
export enum PhaseStatus {
  NOT_STARTED = 'not_started',
  IN_PROGRESS = 'in_progress',
  NEEDS_CLARIFICATION = 'needs_clarification',
  BLOCKED = 'blocked',
  COMPLETED = 'completed',
  APPROVED = 'approved'
}

// Constitution Phase - Core Principles Validation
export interface ConstitutionCheckResult {
  phase: SDDPhase.CONSTITUTION;
  status: PhaseStatus;
  checks: {
    codeQualityFirst: boolean;
    testDrivenDevelopment: boolean;
    userExperienceConsistency: boolean;
    performanceRequirements: boolean;
    specDrivenDevelopment: boolean;
  };
  violations: ConstitutionViolation[];
  approvedAt?: string;
  approvedBy?: string;
}

export interface ConstitutionViolation {
  principle: string;
  description: string;
  justification?: string;
  remediationPlan?: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
}

// Specify Phase - Feature Specification
export interface SpecificationData {
  phase: SDDPhase.SPECIFY;
  status: PhaseStatus;
  title: string;
  description: string;
  userStories: UserStory[];
  functionalRequirements: FunctionalRequirement[];
  successCriteria: SuccessCriterion[];
  keyEntities: KeyEntity[];
  assumptions: string[];
  constraints: string[];
  outOfScope: string[];
  clarificationsNeeded: ClarificationItem[];
  createdAt: string;
  updatedAt: string;
  specFilePath?: string;
}

export interface UserStory {
  id: string;
  title: string;
  description: string;
  actor: string;
  action: string;
  outcome: string;
  acceptanceCriteria: string[];
  priority: 'low' | 'medium' | 'high' | 'critical';
  estimatedEffort?: string;
}

export interface FunctionalRequirement {
  id: string;
  title: string;
  description: string;
  category: string;
  testable: boolean;
  businessValue: string;
  dependencies: string[];
  risks: string[];
}

export interface SuccessCriterion {
  id: string;
  metric: string;
  target: string;
  measurable: boolean;
  technologyAgnostic: boolean;
  userFocused: boolean;
  timeframe?: string;
}

export interface KeyEntity {
  id: string;
  name: string;
  description: string;
  attributes: EntityAttribute[];
  relationships: EntityRelationship[];
}

export interface EntityAttribute {
  name: string;
  type: string;
  required: boolean;
  description: string;
  validation?: string;
}

export interface EntityRelationship {
  relatedEntity: string;
  type: 'one-to-one' | 'one-to-many' | 'many-to-many';
  description: string;
}

export interface ClarificationItem {
  id: string;
  question: string;
  context: string;
  options: ClarificationOption[];
  selectedOption?: string;
  customAnswer?: string;
  resolvedAt?: string;
  resolvedBy?: string;
}

export interface ClarificationOption {
  id: string;
  answer: string;
  implications: string;
}

// Plan Phase - Implementation Planning
export interface PlanData {
  phase: SDDPhase.PLAN;
  status: PhaseStatus;
  technicalContext: TechnicalContext;
  constitutionCheck: ConstitutionCheckResult;
  researchFindings: ResearchFinding[];
  dataModel: DataModel;
  apiContracts: APIContract[];
  quickstart: QuickstartGuide;
  planFilePath?: string;
  createdAt: string;
  updatedAt: string;
}

export interface TechnicalContext {
  technologies: TechnologyChoice[];
  dependencies: Dependency[];
  integrations: Integration[];
  performanceTargets: PerformanceTarget[];
  securityRequirements: SecurityRequirement[];
}

export interface TechnologyChoice {
  category: string;
  technology: string;
  version?: string;
  rationale: string;
  alternatives: string[];
}

export interface Dependency {
  name: string;
  type: 'internal' | 'external' | 'third-party';
  version?: string;
  required: boolean;
  purpose: string;
}

export interface Integration {
  system: string;
  type: 'api' | 'database' | 'service' | 'webhook';
  direction: 'inbound' | 'outbound' | 'bidirectional';
  protocol: string;
  authentication?: string;
}

export interface PerformanceTarget {
  metric: string;
  target: string;
  measurement: string;
  critical: boolean;
}

export interface SecurityRequirement {
  category: string;
  requirement: string;
  implementation: string;
  compliance?: string;
}

export interface ResearchFinding {
  topic: string;
  decision: string;
  rationale: string;
  alternativesConsidered: string[];
  sources: string[];
}

export interface DataModel {
  entities: DataEntity[];
  relationships: DataRelationship[];
  validationRules: ValidationRule[];
  stateTransitions: StateTransition[];
}

export interface DataEntity {
  name: string;
  fields: DataField[];
  primaryKey: string;
  indexes: string[];
  constraints: string[];
}

export interface DataField {
  name: string;
  type: string;
  nullable: boolean;
  defaultValue?: any;
  description: string;
}

export interface DataRelationship {
  from: string;
  to: string;
  type: 'one-to-one' | 'one-to-many' | 'many-to-many';
  foreignKey: string;
  onDelete: 'cascade' | 'restrict' | 'set-null';
}

export interface ValidationRule {
  entity: string;
  field: string;
  rule: string;
  message: string;
}

export interface StateTransition {
  entity: string;
  from: string;
  to: string;
  trigger: string;
  conditions: string[];
}

export interface APIContract {
  id: string;
  method: string;
  path: string;
  description: string;
  parameters: APIParameter[];
  requestBody?: any;
  responses: APIResponse[];
  authentication?: string;
  rateLimit?: string;
}

export interface APIParameter {
  name: string;
  in: 'query' | 'path' | 'header' | 'body';
  type: string;
  required: boolean;
  description: string;
}

export interface APIResponse {
  status: number;
  description: string;
  schema?: any;
}

export interface QuickstartGuide {
  setupSteps: SetupStep[];
  examples: CodeExample[];
  troubleshooting: TroubleshootingItem[];
}

export interface SetupStep {
  order: number;
  title: string;
  description: string;
  commands?: string[];
  notes?: string;
}

export interface CodeExample {
  title: string;
  description: string;
  language: string;
  code: string;
}

export interface TroubleshootingItem {
  problem: string;
  solution: string;
  commonCauses: string[];
}

// Tasks Phase - Development Task Breakdown
export interface TasksData {
  phase: SDDPhase.TASKS;
  status: PhaseStatus;
  taskBreakdown: TaskGroup[];
  estimations: TaskEstimation[];
  dependencies: TaskDependency[];
  timeline: ProjectTimeline;
  createdAt: string;
  updatedAt: string;
}

export interface TaskGroup {
  id: string;
  name: string;
  description: string;
  tasks: DevelopmentTask[];
  estimatedHours: number;
  priority: 'low' | 'medium' | 'high' | 'critical';
}

export interface DevelopmentTask {
  id: string;
  title: string;
  description: string;
  type: 'frontend' | 'backend' | 'database' | 'testing' | 'documentation' | 'devops';
  estimatedHours: number;
  complexity: 'simple' | 'medium' | 'complex';
  skills: string[];
  acceptanceCriteria: string[];
  testingRequirements: string[];
  dependencies: string[];
}

export interface TaskEstimation {
  taskId: string;
  optimistic: number;
  realistic: number;
  pessimistic: number;
  confidence: number;
  assumptions: string[];
}

export interface TaskDependency {
  fromTask: string;
  toTask: string;
  type: 'finish-to-start' | 'start-to-start' | 'finish-to-finish';
  lag?: number;
}

export interface ProjectTimeline {
  phases: TimelinePhase[];
  milestones: TimelineMilestone[];
  criticalPath: string[];
  totalDuration: number;
}

export interface TimelinePhase {
  name: string;
  startDate: string;
  endDate: string;
  tasks: string[];
}

export interface TimelineMilestone {
  id: string;
  name: string;
  date: string;
  deliverables: string[];
  criteria: string[];
}

// Combined Project SDD State
export interface ProjectSDDState {
  projectId?: string;
  constitution: ConstitutionCheckResult;
  specification: SpecificationData;
  plan: PlanData;
  tasks: TasksData;
  currentPhase: SDDPhase;
  overallStatus: PhaseStatus;
  clientApprovalRequired: boolean;
  clientApprovalReceived: boolean;
  readyForImplementation: boolean;
  specKitIntegration: SpecKitIntegration;
  createdAt: string;
  updatedAt: string;
}

// Spec-kit Integration
export interface SpecKitIntegration {
  enabled: boolean;
  branchName?: string;
  specFilePath?: string;
  planFilePath?: string;
  repositoryUrl?: string;
  commands: SpecKitCommand[];
  generatedFiles: GeneratedFile[];
}

export interface SpecKitCommand {
  command: string;
  phase: SDDPhase;
  executed: boolean;
  executedAt?: string;
  output?: string;
  error?: string;
}

export interface GeneratedFile {
  path: string;
  type: 'spec' | 'plan' | 'contract' | 'documentation';
  generatedBy: string;
  generatedAt: string;
  content?: string;
}

// Project Creation Form Data (Enhanced)
export interface EnhancedProjectFormData {
  // Basic Information (existing)
  name: string;
  description: string;
  clientId: string;
  projectType: string;
  priority: string;
  
  // Timeline & Budget (existing)
  startDate: string;
  estimatedEndDate: string;
  budget: number;
  billableHours: boolean;
  hourlyRate: number;
  
  // Team & Resources (enhanced)
  projectManagerId: string;
  teamMemberIds: string[];
  requiredSkills: string[];
  
  // Project Settings (existing)
  isActive: boolean;
  visibility: 'private' | 'team' | 'client';
  notificationsEnabled: boolean;
  
  // SDD Workflow Configuration (new)
  enableSDDWorkflow: boolean;
  enableSpecKitIntegration: boolean;
  clientGuidedWorkflow: boolean;
  requireClientApproval: boolean;
  
  // Initial SDD Data (new)
  sddState: ProjectSDDState;
}

// Client Guidance Configuration
export interface ClientGuidanceConfig {
  enableConstitutionEducation: boolean;
  enableSpecificationGuidance: boolean;
  enablePlanReview: boolean;
  allowClientEditing: boolean;
  notificationPreferences: NotificationPreference[];
}

export interface NotificationPreference {
  phase: SDDPhase;
  method: 'email' | 'dashboard' | 'sms';
  enabled: boolean;
}

// Form Validation States
export interface SDDValidationState {
  phase: SDDPhase;
  isValid: boolean;
  errors: ValidationError[];
  warnings: ValidationWarning[];
  blockers: ValidationBlocker[];
}

export interface ValidationError {
  field: string;
  message: string;
  severity: 'error' | 'warning' | 'info';
}

export interface ValidationWarning {
  field: string;
  message: string;
  suggestion?: string;
}

export interface ValidationBlocker {
  reason: string;
  description: string;
  resolution: string;
}